import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  ClassificazionePercorsoList,
  CreateServizioRequest,
  festivitaCaledarServiziDTO,
  RequestUtenzeAssociate,
  RequestUtenzeCoinvolteList,
  ResponseUtenzeAssociateDettaglio,
  ServiziEERDTO,
  ServiziListInterface,
  ServizioDTO,
  UtenzeCoinvolteList,
} from '@core/models/api/swagger-collection-planner';
import {
  ListCategoriaUtenza,
  ListClassificazioneUtenza,
  ListStatiUtenza,
  ListUtenza,
} from '@core/models/api/swagger-user';
import {
  VehiclePositionRequestBody,
  VehicleTargheCategory,
} from '@core/models/api/swagger-vehicle-fleet/vehicle.interface';
import {
  RequestTableOperatori,
  ResponseOperatoriList,
} from '@core/models/api/swagger-workforce';
import { ListZoneByTipologiaZone } from '@core/models/api/swagger-zonation/tipologie-zone.interface';
import { ZonazioneDTO } from '@core/models/api/swagger-zonation/zone.interface';
import { GenericTableRequest } from '@core/models/shared';
import { ApiService } from '@core/services/api.service';
import { AuthStore } from '@core/store/auth/auth.store';
import { IDRISORSESIDENAV } from '@core/utils/const';
import { canWrite } from '@core/utils/utils';
import {
  signalStore,
  withMethods,
  withState,
  withComputed,
  patchState,
  withProps,
} from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

type ListaServiziState = {
  utenzeList: ListUtenza[];
  operatoriList: ResponseOperatoriList[];
  totalOperatori: number;
  statiUtenza: ListStatiUtenza[];
  servizioDTO: ServizioDTO | null;
  servizioById: ServizioDTO | null;
  servizioToEdit: ServizioDTO | null;
  serviziList: ServiziListInterface[];
  classificazioniOpt: ListClassificazioneUtenza[];
  tipologiaZoneList: ZonazioneDTO[];
  zoneByTipologia: ListZoneByTipologiaZone | null;
  categorieOpt: ListCategoriaUtenza[];
  classificazionePercorso: ClassificazionePercorsoList[];
  utenzeCoinvolteList: UtenzeCoinvolteList[];
  vehicleTargheCategoryList: VehicleTargheCategory | null;
  totalElements: number;
  totalElementsUtenzeCoinvolte: number;
  loader: boolean;
  indexTab: number;
  loaderList: boolean;
  festivitaNazionaliList: festivitaCaledarServiziDTO[];
  eerListNewServizio: ServiziEERDTO[];
  utezeAssociateDettaglioList: ResponseUtenzeAssociateDettaglio | null;
  servizioDaDuplicare: any | null;
};

const SESSION_STORAGE_KEY_DUPLICA = 'servizioDaDuplicare';

const loadServizioDaDuplicareFromSession = (): any | null => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const saved = window.sessionStorage.getItem(SESSION_STORAGE_KEY_DUPLICA);
      return saved ? JSON.parse(saved) : null;
    }
  } catch (e) {
    console.error('Errore durante il recupero di servizioDaDuplicare dalla sessione', e);
  }
  return null;
};

export const initialListaServiziStore: ListaServiziState = {
  utenzeList: [],
  operatoriList: [],
  totalOperatori: 0,
  serviziList: [],
  categorieOpt: [],
  classificazionePercorso: [],
  servizioDTO: null,
  utenzeCoinvolteList: [],
  tipologiaZoneList: [],
  zoneByTipologia: null,
  vehicleTargheCategoryList: null,
  servizioById: null,
  servizioToEdit: null,
  statiUtenza: [],
  classificazioniOpt: [],
  loader: false,
  totalElements: 0,
  totalElementsUtenzeCoinvolte: 0,
  indexTab: 0,
  loaderList: false,
  festivitaNazionaliList: [],
  eerListNewServizio: [],
  utezeAssociateDettaglioList: null,
  servizioDaDuplicare: loadServizioDaDuplicareFromSession(),
};

export const ServiziStore = signalStore(
  { providedIn: 'root' },
  withState(initialListaServiziStore),
  withProps(() => ({
    messageService: inject(MessageService),
    apiService: inject(ApiService),
    router: inject(Router),
    translateService: inject(TranslateService),
    authStore: inject(AuthStore),
  })),
  withComputed((store) => ({
    canWrite: computed(() => canWrite(IDRISORSESIDENAV.servizi_list)),
    statiUtenzaTable: computed(() => {
      const stati = store.statiUtenza();
      return stati.map((stato) => ({
        value: stato.idStato,
        name: stato.descrizione,
        color: stato.hex,
      }));
    }),
    classificazioneUtenzaTable: computed(() => {
      const stati = store.classificazioniOpt();
      return stati.map((stato) => ({
        value: stato.idClassificazione,
        name: stato.descrizione,
      }));
    }),
  })),
  withMethods((store) => ({
    updateIndexTab: (number: number) => {
      patchState(store, { indexTab: number });
    },
    updateServizioToEdit: (partial: Partial<ServizioDTO>) => {
      const current = store.servizioToEdit();
      if (current) {
        patchState(store, {
          servizioToEdit: { ...current, ...partial } as ServizioDTO,
        });
      }
    },
    // api
    fetchOperatoriList: async (requestBody: RequestTableOperatori) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerWorkforceService.fetchOperatore(requestBody),
        );
        if (response) {
          patchState(store, {
            operatoriList: response.content,
            totalOperatori: response.totalElements || 0, // Fallback if API field varies
            loader: false,
          });
        } else {
          patchState(store, {
            operatoriList: [],
            totalOperatori: 0,
            loader: false,
          });
        }
      } catch {
        patchState(store, { operatoriList: [], loader: false });
      }
    },
    fetchServiziList: async (request: any) => {
      try {
        (patchState(store), { loaderList: true }); // Attiva il loader
        // Chiamata al servizio per ottenere i dettagli del cliente (GET)
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.fetchServiziList(
            request,
          ),
        );

        if (response) {
          patchState(store, {
            serviziList: response.content,
            totalElements: response.totalElements,
            loaderList: false,
          });
        }
      } catch {
        patchState(store, { loaderList: false });
      }
    },
    getClassificazionePercorso: async (idTipologiaServizio: number) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.fetchClassificazionePercorso(
            idTipologiaServizio,
          ),
        );
        if (response) {
          patchState(store, {
            classificazionePercorso: response,
            loader: false,
          });
        } else {
          patchState(store, { classificazionePercorso: [], loader: false });
        }
      } catch {
        patchState(store, { classificazionePercorso: [], loader: false });
      }
    },
    getAllCategorie: async () => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.getCategorieUtenze(),
        );
        if (response) {
          patchState(store, { categorieOpt: response, loader: false });
        } else {
          patchState(store, { categorieOpt: [], loader: false });
        }
      } catch {
        patchState(store, { categorieOpt: [], loader: false });
      }
    },
    getLastPositionVehicleByCantiere: async (
      requestBodyParameter: VehiclePositionRequestBody,
    ) => {
      try {
        const response = await firstValueFrom(
          store.apiService.swaggerVehicleFleetService.getLastPositionVehicleByCantiere(
            requestBodyParameter,
          ),
        );
        if (response) {
          patchState(store, { vehicleTargheCategoryList: response.data });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    fetchListAllTipologiaZone: async (request: GenericTableRequest) => {
      try {
        patchState(store); // Attiva il loader
        // Chiamata al servizio per ottenere i dettagli del cliente (GET)
        const response = await firstValueFrom(
          store.apiService.swaggerZonationService.getAllTipologiaZone(request),
        );

        if (response) {
          patchState(store, { tipologiaZoneList: response.content });
        }
      } catch {
        patchState(store); // Disattiva il loader in caso di errore
      }
    },
    fetchZoneByTipologia: async (idTipologia: number) => {
      try {
        patchState(store); // Attiva il loader
        // Chiamata al servizio per ottenere i dettagli del cliente (GET)
        const response = await firstValueFrom(
          store.apiService.swaggerZonationService.fetchZoneByTipologia(
            idTipologia,
          ),
        );

        if (response) {
          patchState(store, { zoneByTipologia: response });
        }
      } catch {
        patchState(store); // Disattiva il loader in caso di errore
      }
    },
    fetchUtenzeCoinvolteList: async (
      requestBody: RequestUtenzeCoinvolteList,
    ) => {
      try {
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.fetchUtenzeCoinvolteList(
            requestBody,
          ),
        );
        if (response) {
          patchState(store, {
            utenzeCoinvolteList: response.contenuto,
            totalElementsUtenzeCoinvolte: response.totale,
          });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    getAllStatiUtenza: async () => {
      try {
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.getStatiUtenza(),
        );

        if (response) {
          patchState(store, { statiUtenza: response });
        } else {
          patchState(store, { statiUtenza: [] });
        }
      } catch {
        patchState(store, { statiUtenza: [] });
      }
    },
    createServizio: async (requestBody: CreateServizioRequest) => {
      try {
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.createNewServizio(
            requestBody,
          ),
        );

        patchState(store, { servizioDTO: response });
      } catch {
        patchState(store, { servizioDTO: null });
      }
    },
    getAllClassificazioni: async () => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.getClassificazioneUtenza(),
        );
        if (response) {
          patchState(store, {
            classificazioniOpt: response,
            loader: false,
          });
        } else {
          patchState(store, { classificazioniOpt: [], loader: false });
        }
      } catch {
        patchState(store, { classificazioniOpt: [], loader: false });
      }
    },
    getServizioById: async (idServizio: number) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.fetchServizioById(
            idServizio,
          ),
        );
        if (response) {
          patchState(store, {
            servizioById: response,
            servizioToEdit: response,
            loader: false,
          });
        } else {
          patchState(store, {
            servizioById: null,
            servizioToEdit: null,
            loader: false,
          });
        }
      } catch {
        patchState(store, { servizioById: null, loader: false });
      }
    },
    getServizioDaDuplicareById: async (
      idServizio: number,
    ): Promise<any | null> => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.fetchServizioById(
            idServizio,
          ),
        );
        if (response) {
          try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
              window.sessionStorage.setItem(
                SESSION_STORAGE_KEY_DUPLICA,
                JSON.stringify(response),
              );
            }
          } catch (e) {
            console.error('Errore salvataggio servizioDaDuplicare in session', e);
          }
          patchState(store, {
            servizioDaDuplicare: response,
            loader: false,
          });
          return response;
        } else {
          try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
              window.sessionStorage.removeItem(SESSION_STORAGE_KEY_DUPLICA);
            }
          } catch {}
          patchState(store, {
            servizioDaDuplicare: null,
            loader: false,
          });
          return null;
        }
      } catch {
        try {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.removeItem(SESSION_STORAGE_KEY_DUPLICA);
          }
        } catch {}
        patchState(store, { servizioDaDuplicare: null, loader: false });
        return null;
      }
    },
    setServizioDaDuplicare: (servizio: any | null) => {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          if (servizio) {
            window.sessionStorage.setItem(
              SESSION_STORAGE_KEY_DUPLICA,
              JSON.stringify(servizio),
            );
          } else {
            window.sessionStorage.removeItem(SESSION_STORAGE_KEY_DUPLICA);
          }
        }
      } catch (e) {
        console.error('Errore setServizioDaDuplicare in session', e);
      }
      patchState(store, { servizioDaDuplicare: servizio });
    },
    clearServizioDaDuplicare: () => {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.removeItem(SESSION_STORAGE_KEY_DUPLICA);
        }
      } catch (e) {
        console.error('Errore clearServizioDaDuplicare in session', e);
      }
      patchState(store, { servizioDaDuplicare: null });
    },
    fetchFestivitaCalendario: async (rangeDate: { da: string; a: string }) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.fetchFestivitaCalendario(
            rangeDate,
          ),
        );
        if (response) {
          patchState(store, {
            festivitaNazionaliList: response,
            loader: false,
          });
        } else {
          patchState(store, { servizioById: null, loader: false });
        }
      } catch {
        patchState(store, { servizioById: null, loader: false });
      }
    },
    fetchFrazioni: async () => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.fetchEERServizi(),
        );
        if (response) {
          patchState(store, {
            eerListNewServizio: response,
            loader: false,
          });
        } else {
          patchState(store, { servizioById: null, loader: false });
        }
      } catch {
        patchState(store, { servizioById: null, loader: false });
      }
    },
    fetchUtenzeAssociateDettaglio: async (
      idServizio: number,
      requestBody: RequestUtenzeAssociate | null,
    ) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.fetchUtenzeAssociate(
            idServizio,
            requestBody,
          ),
        );
        if (response) {
          patchState(store, {
            utezeAssociateDettaglioList: response,
            loader: false,
          });
        } else {
          patchState(store, { servizioById: null, loader: false });
        }
      } catch {
        patchState(store, { servizioById: null, loader: false });
      }
    },
    patchUtenzeAssociateDettaglio: async (
      idServizio: number,
      requestBody: { idUtenze: number[]; selezionata: boolean },
    ) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.patchUtenzeAssociate(
            idServizio,
            requestBody,
          ),
        );
        if (response) {
          patchState(store, {
            loader: false,
          });

          store.messageService.add({
            severity: 'success',
            summary: store.translateService.instant(
              'shared_message_api.success',
            ),
            detail: store.translateService.instant(
              'shared_message_api.message_success',
            ),
          });
        } else {
          patchState(store, { loader: false });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    patchServizio: async (idServizio: number, requestBody: any) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.patchServizio(
            idServizio,
            requestBody,
          ),
        );
        if (response) {
          patchState(store, {
            servizioDTO: response,
            loader: false,
          });

          store.messageService.add({
            severity: 'success',
            summary: store.translateService.instant(
              'shared_message_api.success',
            ),
            detail: store.translateService.instant(
              'shared_message_api.message_success',
            ),
          });
        } else {
          patchState(store, { loader: false });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    patchSospendiServizio: async (
      idServizio: number,
      dateFormatted: string,
    ) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.patchSospendiServizio(
            idServizio,
            { dataSospensione: dateFormatted },
          ),
        );
        patchState(store, { loader: false });

        store.messageService.add({
          severity: 'success',
          summary: store.translateService.instant('shared_message_api.success'),
          detail: store.translateService.instant(
            'shared_message_api.message_success',
          ),
        });
        return response;
      } catch {
        patchState(store, { loader: false });
        return null;
      }
    },
    patchRiattivaServizio: async (
      idServizio: number,
      dateFormatted: string,
    ) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.patchRiattivaServizio(
            idServizio,
            { dataRipresa: dateFormatted },
          ),
        );
        patchState(store, { loader: false });

        store.messageService.add({
          severity: 'success',
          summary: store.translateService.instant('shared_message_api.success'),
          detail: store.translateService.instant(
            'shared_message_api.message_success',
          ),
        });
        return response;
      } catch {
        patchState(store, { loader: false });
        return null;
      }
    },
    patchCessaServizio: async (idServizio: number, dateFormatted: string) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.patchCessaServizio(
            idServizio,
            { dataCessazione: dateFormatted },
          ),
        );
        patchState(store, { loader: false });

        store.messageService.add({
          severity: 'success',
          summary: store.translateService.instant('shared_message_api.success'),
          detail: store.translateService.instant(
            'shared_message_api.message_success',
          ),
        });
        return response;
      } catch {
        patchState(store, { loader: false });
        return null;
      }
    },
    patchAnnullaServizio: async (idServizio: number) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerCollectionPlannerServiceService.patchAnnullaServizio(
            idServizio,
          ),
        );
        patchState(store, { loader: false });

        store.messageService.add({
          severity: 'success',
          summary: store.translateService.instant('shared_message_api.success'),
          detail: store.translateService.instant(
            'shared_message_api.message_success',
          ),
        });
        return response;
      } catch {
        patchState(store, { loader: false });
        return null;
      }
    },
  })),
);
